import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Home as HomeIcon, Search, Plus, Calendar, MessageCircle, User, MapPin, ChevronLeft, ChevronRight } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { CATEGORIES } from '../lib/constants'
import { track } from '../lib/analytics'
import { useAuth } from '../context/AuthContext'
import ListingCard from '../components/ListingCard'
import Header from '../components/Header'
import ArrangeListings from '../components/ArrangeListings'
import { recommendedSort, adminOrder } from '../lib/listingOrder'
import { areaFor } from '../lib/areas'
import WantedStrip from '../components/WantedStrip'

const UTM_CAMPAIGN_TO_CAT = {
camping: 5,
party: 4,
tradie: 1,
tools: 1,
garden: 2,
kids: 6,
bbq: 30,
sports: 7,
events: 4,
boats: 22,
}

const RADIUS_OPTIONS = [
{ label: 'Any distance', value: null },
{ label: 'Within 5km', value: 5 },
{ label: 'Within 10km', value: 10 },
{ label: 'Within 20km', value: 20 },
{ label: 'Within 50km', value: 50 },
{ label: 'Within 100km', value: 100 },
]

const SORT_OPTIONS = [
{ label: 'Recommended', value: 'recommended' },
{ label: 'Nearest first', value: 'distance' },
{ label: 'Newest first', value: 'newest' },
{ label: 'Cheapest first', value: 'cheapest' },
{ label: 'Most expensive', value: 'expensive' },
{ label: 'Highest rated', value: 'rated' },
]

const CASCADE_RADII = [5, 25, 50, 100]

function getDistanceKm(lat1, lng1, lat2, lng2) {
const R = 6371
const dLat = (lat2 - lat1) * Math.PI / 180
const dLng = (lng2 - lng1) * Math.PI / 180
const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
Math.sin(dLng/2) * Math.sin(dLng/2)
return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
}

async function geocodePostcode(postcode) {
const apiKey = 'AIzaSyB4HNjEiIqfigiVSCDBpSxGSFmrPlPs-6o'
if (!apiKey) return null
try {
const res = await fetch(
`https://maps.googleapis.com/maps/api/geocode/json?address=${postcode}+Australia&key=${apiKey}`
)
const data = await res.json()
if (data.results && data.results[0]) {
const loc = data.results[0].geometry.location
return { lat: loc.lat, lng: loc.lng }
}
console.error('Geocode returned no results:', { address: postcode, status: data.status, error_message: data.error_message, resultCount: data.results?.length })
} catch (err) {
console.error('Geocode error:', err)
}
return null
}

export default function Home() {
const { user, profile } = useAuth()
const isAdmin = !!profile?.is_admin
const [arrangeList, setArrangeList] = useState(null) // admin 'Arrange listings' mode when set
const [listings, setListings] = useState([])
const [loading, setLoading] = useState(true)
const [keyword, setKeyword] = useState('')
const [location, setLocation] = useState('')
const [activeCat, setActiveCat] = useState(null)
const [radius, setRadius] = useState(null)
const [sort, setSort] = useState('recommended')
const [userLocation, setUserLocation] = useState(null)
const [detectedLocation, setDetectedLocation] = useState(null)
const [locationLoading, setLocationLoading] = useState(false)
const [autoExpandedRadius, setAutoExpandedRadius] = useState(null)
const [searchCenter, setSearchCenter] = useState(null)
const [includesOperatorOnly, setIncludesOperatorOnly] = useState(false)
const [favouriteSet, setFavouriteSet] = useState(new Set())
const [favouritesLoaded, setFavouritesLoaded] = useState(false)
const [promoExpanded, setPromoExpanded] = useState(false)
const catStripRef = useRef(null)
const lastSearchRef = useRef(null)
const [catScroll, setCatScroll] = useState({ left: false, right: false })
const [searchParams] = useSearchParams()
const navigate = useNavigate()

useEffect(() => {
const cat = searchParams.get('cat')
const utmCampaign = searchParams.get('utm_campaign')
const catFromUtm = utmCampaign ? (UTM_CAMPAIGN_TO_CAT[utmCampaign.toLowerCase()] ?? null) : null
const catId = cat ? parseInt(cat) : catFromUtm
if (catId) setActiveCat(catId)
if (utmCampaign) track('campaign_visit', { campaign: utmCampaign, source: searchParams.get('utm_source') || 'unknown', medium: searchParams.get('utm_medium') || 'unknown' })

const GEO_TIMEOUT_MS = 1000
let cancelled = false

const geoPromise = fetch('/api/geo')
.then(r => r.ok ? r.json() : null)
.catch(() => null)
const timeoutPromise = new Promise(resolve => setTimeout(() => resolve(null), GEO_TIMEOUT_MS))

Promise.race([geoPromise, timeoutPromise]).then(g => {
if (cancelled) return
let geoCenter = null
if (g && isFinite(g.lat) && isFinite(g.lng)) {
geoCenter = { lat: g.lat, lng: g.lng }
setDetectedLocation({ lat: g.lat, lng: g.lng, city: g.city || null })
track('geo_detect_success', { city: g.city || 'unknown' })
} else {
track('geo_detect_failed')
}
fetchListings(catId, '', '', null, geoCenter, 'recommended', true)
})

return () => { cancelled = true }
// eslint-disable-next-line
}, [])

useEffect(() => {
if (!user) {
setFavouriteSet(new Set())
setFavouritesLoaded(true)
return
}
setFavouritesLoaded(false)
;(async () => {
const { data } = await supabase
.from('favourites')
.select('listing_id')
.eq('user_id', user.id)
setFavouriteSet(new Set((data || []).map(f => f.listing_id)))
setFavouritesLoaded(true)
})()
}, [user])

const sortKey = (l) => {
if (l.price_per_hour) return l.price_per_hour
if (l.price_per_day) return l.price_per_day / 24
if (l.price_per_week) return l.price_per_week / 168
return Infinity
}

const sortListings = (data, sortBy, center = null) => {
let results = [...data]
switch (sortBy) {
case 'recommended':
// Admin order within distance groups (0–15 / 15–40 / 40–100 / 100+ km)
return recommendedSort(results, center ? l => (l.lat && l.lng ? getDistanceKm(center.lat, center.lng, l.lat, l.lng) : null) : null)
case 'distance':
if (!center) return results
return results.sort((a, b) => {
const da = (a.lat && a.lng) ? getDistanceKm(center.lat, center.lng, a.lat, a.lng) : Infinity
const db = (b.lat && b.lng) ? getDistanceKm(center.lat, center.lng, b.lat, b.lng) : Infinity
return da - db
})
case 'cheapest':
return results.sort((a, b) => sortKey(a) - sortKey(b))
case 'expensive':
return results.sort((a, b) => sortKey(b) - sortKey(a))
case 'rated':
return results.sort((a, b) => (b.profiles?.rating || 0) - (a.profiles?.rating || 0))
default:
return results
}
}

const filterByRadius = (data, center, rad) => {
if (!center || !rad) return data
return data.filter(l => {
if (!l.lat || !l.lng) return false
return getDistanceKm(center.lat, center.lng, l.lat, l.lng) <= rad
})
}

const fetchListings = async (catId = activeCat, kw = keyword, loc = location, rad = radius, userLoc = userLocation, sortBy = sort, skipRadiusFilter = false, opOnly = includesOperatorOnly) => {
setLoading(true)
lastSearchRef.current = { term: kw?.trim() || '', location: loc?.trim() || '', category: CATEGORIES.find(c => c.id === catId)?.slug || '', logged: false }
setAutoExpandedRadius(null)

let data = null

if (kw && kw.trim()) {
// Use fuzzy search RPC for typo tolerance
const { data: fuzzyData } = await supabase.rpc('search_listings_fuzzy', {
search_term: kw.trim()
})
// Need to manually fetch profiles for fuzzy results
if (fuzzyData && fuzzyData.length > 0) {
const ownerIds = [...new Set(fuzzyData.map(l => l.owner_id))]
const { data: profilesData } = await supabase
.from('public_profiles')
.select('id, full_name, rating, review_count, created_at, founding_lister_number, founding_lister_suburb')
.in('id', ownerIds)
const profilesMap = {}
;(profilesData || []).forEach(p => { profilesMap[p.id] = p })
data = fuzzyData.map(l => ({ ...l, profiles: profilesMap[l.owner_id] || null }))
data = data.filter(l => !l.archived_at && l.status === 'active' && !l.admin_disabled)
if (catId) data = data.filter(l => l.category_id === catId)
if (opOnly) data = data.filter(l => l.includes_operator)
} else {
data = []
}
} else {
let query = supabase
.from('listings')
.select('*')
.is('archived_at', null)
.eq('status', 'active')
.eq('admin_disabled', false)
.order('created_at', { ascending: false })
if (catId) query = query.eq('category_id', catId)
if (opOnly) query = query.eq('includes_operator', true)
const result = await query
const _listings = result.data || []
const _oids = [...new Set(_listings.map(l => l.owner_id).filter(Boolean))]
let _omap = {}
if (_oids.length) {
const { data: _op } = await supabase.from('public_profiles').select('id, full_name, rating, review_count, created_at, founding_lister_number, founding_lister_suburb').in('id', _oids)
;(_op || []).forEach(pp => { _omap[pp.id] = pp })
}
data = _listings.map(l => ({ ...l, profiles: _omap[l.owner_id] || null }))
}

const allResults = data || []

let center = null
if (loc && loc.trim()) {
const isPostcode = /^\d{4}$/.test(loc.trim())
if (isPostcode) {
center = await geocodePostcode(loc.trim())
} else {
const matchingByName = allResults.filter(l => {
const term = loc.trim().toLowerCase()
return (l.suburb && l.suburb.toLowerCase().includes(term)) ||
(l.state && l.state.toLowerCase().includes(term)) ||
(l.full_address && l.full_address.toLowerCase().includes(term))
})
if (matchingByName.length > 0) {
setListings(sortListings(matchingByName, sortBy, null))
setSearchCenter(null)
setLoading(false)
return
}
center = await geocodePostcode(loc.trim())
}
} else if (userLoc) {
center = userLoc
} else if (detectedLocation) {
// IP-detected location (from first load) — only used to order results, never to filter them out
center = { lat: detectedLocation.lat, lng: detectedLocation.lng }
skipRadiusFilter = true
}

setSearchCenter(center)

if (center && skipRadiusFilter) {
setListings(sortListings(allResults, sortBy, center))
setLoading(false)
return
}

if (center) {
// Try the chosen radius first (if any); if empty, cascade to larger radii, then 'any'
const initialFiltered = rad ? filterByRadius(allResults, center, rad) : null
if (initialFiltered && initialFiltered.length > 0) {
setListings(sortListings(initialFiltered, sortBy, center))
} else {
const cascadeRadii = rad ? CASCADE_RADII.filter(r => r > rad) : CASCADE_RADII
let found = []
let usedRadius = null
for (const r of cascadeRadii) {
const filtered = filterByRadius(allResults, center, r)
if (filtered.length > 0) {
found = filtered
usedRadius = r
break
}
}
if (found.length > 0) {
setAutoExpandedRadius(usedRadius)
setListings(sortListings(found, sortBy, center))
} else {
// Nothing at any cascade tier — fall back to all Australia-wide listings
setAutoExpandedRadius('any')
setListings(sortListings(allResults, sortBy, center))
}
}
} else {
setListings(sortListings(allResults, sortBy, null))
}
setLoading(false)
}

const getUserLocation = (callback) => {
setLocationLoading(true)
navigator.geolocation.getCurrentPosition(
(pos) => {
const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude }
setUserLocation(loc)
setLocationLoading(false)
if (callback) callback(loc)
},
() => {
setLocationLoading(false)
alert('Could not get your location. Please allow location access or search by postcode.')
}
)
}

const handleRadiusChange = (val) => {
const newRadius = val ? parseInt(val) : null
setRadius(newRadius)
if (newRadius && !userLocation && !location.trim()) {
getUserLocation((loc) => fetchListings(activeCat, keyword, location, newRadius, loc, sort))
} else {
fetchListings(activeCat, keyword, location, newRadius, userLocation, sort)
}
}

const startArranging = async () => {
const { data } = await supabase
.from('listings')
.select('id, title, images, suburb, state, category_id, admin_rank, created_at')
.is('archived_at', null)
.eq('status', 'active')
.eq('admin_disabled', false)
.order('created_at', { ascending: false })
track('admin_arrange_open')
setArrangeList(adminOrder(data || []))
}

const finishArranging = () => {
setArrangeList(null)
fetchListings(activeCat, keyword, location, radius, userLocation, sort)
}

const handleSortChange = (val) => {
setSort(val)
fetchListings(activeCat, keyword, location, radius, userLocation, val)
}

const updateCatScroll = () => {
const el = catStripRef.current
if (!el) return
setCatScroll({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 })
}
const scrollCats = (dir) => catStripRef.current?.scrollBy({ left: dir * catStripRef.current.clientWidth * 0.8 })

useEffect(() => {
updateCatScroll()
window.addEventListener('resize', updateCatScroll)
return () => window.removeEventListener('resize', updateCatScroll)
// eslint-disable-next-line
}, [])

// Admin "Searches with no results": log real searches (a keyword, place or
// category) that end with nothing to show. No personal details are sent.
useEffect(() => {
const s = lastSearchRef.current
if (loading || listings.length > 0 || !s || s.logged || !(s.term || s.location || s.category)) return
s.logged = true
supabase.rpc('log_search_miss', { p_term: s.term, p_location: s.location, p_category: s.category }).then(() => {})
track('search_no_results', { search_term: s.term.slice(0, 80), category: s.category })
// eslint-disable-next-line
}, [loading, listings])

// Wanted board links: pre-filled from what they searched for, and the
// area they arrived from (area short links land on /?utm_campaign=<area>)
const wantedPostUrl = () => `/wanted/new?${new URLSearchParams({
...(keyword.trim() ? { title: keyword.trim() } : {}),
...(location.trim() ? { loc: location.trim() } : {}),
...(activeCat ? { cat: String(activeCat) } : {}),
})}`
const arrivedArea = (() => { const slug = searchParams.get('utm_campaign'); return areaFor(slug) ? { slug: slug.toLowerCase(), ...areaFor(slug) } : null })()
const hasSearch = !!(keyword.trim() || location.trim() || activeCat)
const wantedPrompt = (position) => (
<div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
<div style={{ fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>Nothing nearby yet — post this as a Wanted request?</div>
<button className="btn btn-primary" onClick={() => { track('wanted_prompt_click', { position }); navigate(wantedPostUrl()) }}>
Post {keyword.trim() ? `"${keyword.trim().slice(0, 40)}"` : 'a request'} as Wanted
</button>
</div>
)

const handleSearch = () => fetchListings(activeCat, keyword, location, radius, userLocation, sort)

const handleCat = (id) => {
const next = activeCat === id ? null : id
setActiveCat(next)
fetchListings(next, keyword, location, radius, userLocation, sort)
}

const searchLabel = () => {
let parts = []
if (keyword) parts.push(`"${keyword}"`)
if (location) parts.push(`near ${location}`)
if (radius && searchCenter) parts.push(`within ${radius}km`)
if (activeCat) parts.push(CATEGORIES.find(c => c.id === activeCat)?.name)
if (includesOperatorOnly) parts.push('with operator')
return parts.length > 0 ? parts.join(' · ') : 'All listings'
}

const pillSelectStyle = {
padding: '8px 14px',
borderRadius: 99,
border: '1px solid rgba(255,255,255,0.35)',
background: 'rgba(255,255,255,0.12)',
color: '#fff',
fontSize: 13,
fontWeight: 500,
cursor: 'pointer',
backdropFilter: 'blur(8px)',
}

const purpleSearchBtn = {
padding: '12px 32px',
borderRadius: 99,
border: 'none',
background: '#5B2EBF',
color: '#fff',
fontSize: 16,
fontWeight: 600,
cursor: 'pointer',
display: 'inline-flex',
alignItems: 'center',
gap: 8,
boxShadow: '0 4px 12px rgba(91, 46, 191, 0.4)',
}

const expandedBannerStyle = {
background: 'linear-gradient(135deg, #2D3FCC 0%, #7B3FE4 100%)',
border: 'none',
borderRadius: 8,
padding: '14px 20px',
marginBottom: 16,
fontSize: 14,
color: '#fff',
textAlign: 'center',
boxShadow: '0 4px 12px rgba(45, 63, 204, 0.25)',
}

return (
<div>
<Header />
<div className="search-wrap" style={{ position: 'relative', overflow: 'hidden' }}>

<div style={{ textAlign: 'center', marginBottom: '2rem', position: 'relative', zIndex: 1 }}>
<img
src="/hireit-logo-circle.png"
alt="HireIt"
style={{ height: 'clamp(100px, 14vw, 130px)', width: 'auto', margin: '0 auto 16px', borderRadius: '50%', filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.15))' }}
/>
<h1 style={{
color: '#fff',
fontFamily: "'Plus Jakarta Sans', sans-serif",
fontWeight: 800,
fontSize: 'clamp(28px, 5vw, 48px)',
lineHeight: 1.15,
marginBottom: 12,
letterSpacing: '-0.02em'
}}>
<span style={{ display: 'inline-block' }}>Hire locally.</span>{' '}<span style={{ display: 'inline-block' }}>Earn from what you own.</span>
</h1>
<p style={{ color: 'rgba(255,255,255,0.95)', fontSize: 'clamp(17px, 2.2vw, 20px)', fontWeight: 500, maxWidth: 600, margin: '0 auto', lineHeight: 1.5, textWrap: 'balance' }}>
Find what you need nearby, or list your own gear and earn when you're not using it.
</p>
</div>

<div style={{ marginTop: 20, marginBottom: 28, textAlign: 'center', position: 'relative', zIndex: 1 }}>
{user ? (
<>
<div style={{ color: '#fff', fontSize: 'clamp(16px, 1.8vw, 18px)', fontWeight: 600, lineHeight: 1.4, marginBottom: 14, textWrap: 'balance' }}>
Got something to hire out, or looking for something to hire?
</div>
<div className="hero-actions">
<button
onClick={() => { track('list_item_click', { position: 'hero' }); navigate('/list-item') }}
style={{
background: '#fff',
color: '#5B2EBF',
borderRadius: 99,
padding: '18px 64px',
fontSize: 19,
fontWeight: 700,
border: 'none',
cursor: 'pointer',
boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
}}
>
List an item
</button>
<button
onClick={() => { track('wanted_prompt_click', { position: 'hero' }); navigate('/wanted/new') }}
style={{
background: 'transparent',
color: '#fff',
borderRadius: 99,
padding: '16px 40px',
fontSize: 18,
fontWeight: 600,
border: '2px solid #fff',
cursor: 'pointer',
}}
>
Post a Wanted request
</button>
</div>
</>
) : (
<>
<div style={{ color: '#fff', fontSize: 'clamp(16px, 1.8vw, 18px)', fontWeight: 600, lineHeight: 1.4, marginBottom: 16, textWrap: 'balance' }}>
Got something to hire out, or looking for something to hire?
</div>
<div className="hero-actions">
<button
onClick={() => { track('signup_click', { position: 'hero' }); navigate('/auth?mode=signup&from=hero') }}
style={{
background: '#fff',
color: '#5B2EBF',
borderRadius: 99,
padding: '16px 40px',
fontSize: 18,
fontWeight: 700,
border: 'none',
cursor: 'pointer',
boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
}}
>
Sign up free
</button>
<button
onClick={() => { track('list_item_click', { position: 'hero' }); navigate('/list-item') }}
style={{
background: 'rgba(255,255,255,0.15)',
color: '#fff',
borderRadius: 99,
padding: '16px 40px',
fontSize: 18,
fontWeight: 600,
border: '2px solid rgba(255,255,255,0.5)',
cursor: 'pointer',
backdropFilter: 'blur(8px)',
}}
>
List an item
</button>
<button
onClick={() => { track('wanted_prompt_click', { position: 'hero' }); navigate('/wanted/new') }}
style={{
background: 'transparent',
color: '#fff',
borderRadius: 99,
padding: '16px 40px',
fontSize: 18,
fontWeight: 600,
border: '2px solid #fff',
cursor: 'pointer',
}}
>
Post a Wanted request
</button>
</div>
<div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, marginTop: 12 }}>
Already have an account? <button onClick={() => navigate('/auth')} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0, fontWeight: 500 }}>Sign in</button>
</div>
</>
)}
</div>

<div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 680, margin: '0 auto', position: 'relative', zIndex: 1 }}>
<div className="search-box">
<input
placeholder="Search item..."
value={keyword}
onChange={e => setKeyword(e.target.value)}
onKeyDown={e => e.key === 'Enter' && handleSearch()}
maxLength={100}
style={{ width: '100%' }}
/>
</div>
<div className="search-box" style={{ marginTop: 0 }}>
<input
placeholder="Suburb or postcode..."
value={location}
onChange={e => setLocation(e.target.value)}
onKeyDown={e => e.key === 'Enter' && handleSearch()}
maxLength={100}
/>
<select
value={activeCat || ''}
onChange={e => { setActiveCat(e.target.value ? parseInt(e.target.value) : null); fetchListings(e.target.value ? parseInt(e.target.value) : null, keyword, location, radius, userLocation, sort) }}
style={{ borderLeft: '1px solid var(--border)', padding: '0 8px' }}
>
<option value="">All categories</option>
{CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
</select>
</div>
<div style={{ textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>
Can't find it?{' '}
<button onClick={() => { track('wanted_prompt_click', { position: 'under_search' }); navigate('/wanted/new') }} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0, fontWeight: 600 }}>Post a Wanted request</button>
</div>
</div>

<div style={{ display: 'flex', justifyContent: 'center', marginTop: 16, gap: 8, alignItems: 'center', flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
<select
value={radius || ''}
onChange={e => handleRadiusChange(e.target.value)}
style={pillSelectStyle}
>
{RADIUS_OPTIONS.map(r => (
<option key={r.value} value={r.value || ''} style={{ color: '#000' }}>{r.label}</option>
))}
</select>

<select
value={sort}
onChange={e => handleSortChange(e.target.value)}
style={pillSelectStyle}
>
{SORT_OPTIONS.map(s => (
<option key={s.value} value={s.value} style={{ color: '#000' }}>{s.label}</option>
))}
</select>

<button onClick={() => { track('search_click'); handleSearch() }} style={purpleSearchBtn}>
<Search size={18} /> Search
</button>

<button
onClick={() => {
const next = !includesOperatorOnly
setIncludesOperatorOnly(next)
fetchListings(activeCat, keyword, location, radius, userLocation, sort, false, next)
}}
style={{
padding: '8px 14px',
borderRadius: 99,
border: `1px solid ${includesOperatorOnly ? 'var(--accent)' : 'rgba(255,255,255,0.35)'}`,
background: includesOperatorOnly ? 'var(--accent)' : 'rgba(255,255,255,0.12)',
color: '#fff',
fontSize: 13,
fontWeight: 500,
cursor: 'pointer',
backdropFilter: 'blur(8px)',
}}
>
🛠️ With operator
</button>

{radius && (
<button
onClick={userLocation ? () => fetchListings(activeCat, keyword, location, radius, userLocation, sort) : () => getUserLocation((loc) => fetchListings(activeCat, keyword, location, radius, loc, sort))}
style={{ ...pillSelectStyle, display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none' }}
>
<MapPin size={14} />
{locationLoading ? 'Getting location...' : userLocation ? 'Update location' : 'Use my location'}
</button>
)}
</div>

</div>

<div style={{ background: '#FFFBEA', borderBottom: '2px solid #F59E0B', padding: '10px 16px', textAlign: 'center' }}>
<div className="container" style={{ maxWidth: 680 }}>
<div style={{ display: 'inline-block', background: '#F59E0B', color: '#fff', fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', borderRadius: 99, padding: '3px 12px', marginBottom: 4 }}>
🎉 LAUNCH OFFER
</div>
<div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 28, marginBottom: 4, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
Get rewarded on your first hire 🎉
</div>
<div style={{ color: '#92400E', fontSize: 14, marginBottom: 8, lineHeight: 1.5 }}>
List your first item and complete a hire within 30 days to claim your reward.
</div>
<button
onClick={() => { track('list_item_click', { position: 'promo_banner' }); navigate('/list-item') }}
className="btn btn-gradient"
style={{ marginBottom: 8 }}
>
LIST YOUR ITEM
</button>
<div>
<button
onClick={() => setPromoExpanded(p => !p)}
style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: 12, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
>
{promoExpanded ? 'Hide details ↑' : 'See offer details ↓'}
</button>
{promoExpanded && (
<div style={{ marginTop: 8, fontSize: 13, color: '#78350F', background: '#FDE68A', borderRadius: 8, padding: '10px 14px', maxWidth: 440, margin: '8px auto 0' }}>
Your first qualifying hire is charged at our special launch rate of <strong>8% platform fee</strong> (normally 12%).
</div>
)}
</div>
</div>
</div>

<div style={{ background: '#fff', borderBottom: '1px solid var(--border)', padding: '8px 0' }}>
<div className="container" style={{ position: 'relative' }}>
{catScroll.left && <button className="cat-strip-arrow left" aria-label="Scroll categories left" onClick={() => scrollCats(-1)}><ChevronLeft size={18} /></button>}
{catScroll.right && <button className="cat-strip-arrow right" aria-label="Scroll categories right" onClick={() => scrollCats(1)}><ChevronRight size={18} /></button>}
<div ref={catStripRef} className="cat-strip" onScroll={updateCatScroll}>
<button className={`cat-pill ${!activeCat ? 'active' : ''}`} onClick={() => handleCat(null)}>All</button>
{CATEGORIES.map(c => (
<button key={c.id} className={`cat-pill ${activeCat === c.id ? 'active' : ''}`} onClick={() => handleCat(c.id)}>
{c.icon} {c.name}
</button>
))}
</div>
</div>
</div>

{!arrangeList && <WantedStrip center={userLocation || detectedLocation} />}

<div className="container section">
<div style={{ textAlign: 'center', marginBottom: '1rem' }}>
<h2 className="section-title" style={{ marginBottom: 4 }}>
{searchLabel()}
</h2>
{arrivedArea && (
<div style={{ fontSize: 13, marginTop: 4 }}>
<button onClick={() => navigate(`/wanted/${arrivedArea.slug}`)} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600 }}>🔎 See what people in {arrivedArea.name} are looking for →</button>
</div>
)}
{detectedLocation && (sort === 'distance' || sort === 'recommended') && !location.trim() && !userLocation && (
<div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
📍 {sort === 'recommended' ? 'Listings near you first' : 'Nearest first'} · <button onClick={() => handleSortChange('newest')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 12 }}>Show newest instead</button>
</div>
)}
</div>

{autoExpandedRadius && autoExpandedRadius !== 'any' && (
<div style={expandedBannerStyle}>
ℹ️ No listings found nearby — showing results within <strong>{autoExpandedRadius}km</strong> of {location || 'your location'}.
</div>
)}

{autoExpandedRadius === 'any' && (
<div style={{
background: '#fff',
border: '1px solid var(--border)',
borderRadius: 16,
padding: '2rem 1.5rem',
marginBottom: 20,
textAlign: 'center',
boxShadow: 'var(--shadow-sm)',
}}>
<div style={{ fontSize: 56, marginBottom: 8, lineHeight: 1 }}>🏆</div>
<h3 style={{ color: 'var(--navy)', fontWeight: 700, fontSize: 22, marginBottom: 6, letterSpacing: '-0.01em' }}>
Be the first to list in {location || 'your area'}!
</h3>
<p style={{ color: 'var(--text-muted)', fontSize: 15, marginBottom: 16, lineHeight: 1.5 }}>
Start earning from the gear you own.
</p>
<button className="btn btn-gradient btn-lg" onClick={() => { track('list_item_click', { position: 'empty_state_no_local' }); navigate('/list-item') }}>
List an item
</button>
<div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
ℹ️ No items in {location || 'your area'} yet — showing items from across Australia
</div>
{keyword.trim() && wantedPrompt('no_local')}
</div>
)}

{isAdmin && !arrangeList && (
<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
<button className="btn btn-ghost btn-sm" onClick={startArranging}>↕️ Arrange listings (admin)</button>
</div>
)}

{arrangeList ? (
<ArrangeListings listings={arrangeList} onClose={() => setArrangeList(null)} onSaved={finishArranging} />
) : (loading || (user && !favouritesLoaded)) ? (
<div className="grid-4">
{[1, 2, 3, 4, 5, 6].map(i => (
<div key={i} className="skeleton-card">
<div className="skeleton skeleton-img" />
<div style={{ padding: '1.25rem' }}>
<div className="skeleton skeleton-line tiny" />
<div className="skeleton skeleton-line" />
<div className="skeleton skeleton-line short" />
</div>
</div>
))}
</div>
) : listings.length === 0 && hasSearch ? (
<div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
<div style={{ fontSize: 48, marginBottom: 16, lineHeight: 1 }}>🔎</div>
<div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>
No listings match {keyword.trim() ? `"${keyword.trim().slice(0, 40)}"` : 'your search'}{location.trim() ? ` near ${location.trim()}` : ''}
</div>
<p style={{ marginBottom: 0 }}>Try a different word, or let people nearby know what you need.</p>
{wantedPrompt('no_results')}
</div>
) : listings.length === 0 ? (
<div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
<div style={{ fontSize: 48, marginBottom: 16, lineHeight: 1 }}>📦</div>
<div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>
No listings on HireIt yet
</div>
<p style={{ marginBottom: 20 }}>Be the first to list something and start earning.</p>
<button className="btn btn-gradient btn-lg" onClick={() => { track('list_item_click', { position: 'empty_state_no_listings' }); navigate('/list-item') }}>List an item</button>
</div>
) : (
<div className="grid-4">
{listings.map(l => (
<ListingCard
key={l.id}
listing={l}
isFavourited={favouriteSet.has(l.id)}
distanceKm={searchCenter && l.lat && l.lng ? Math.round(getDistanceKm(searchCenter.lat, searchCenter.lng, l.lat, l.lng)) : null}
isAnyFallback={autoExpandedRadius === 'any'}
/>
))}
</div>
)}
</div>

<div style={{ background: 'var(--primary-pale)', padding: '3.5rem 1rem', textAlign: 'center', borderTop: '1px solid var(--border)' }}>
<h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 10, color: 'var(--navy)' }}>Got something to hire out?</h2>
<p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 16 }}>List your item in minutes and start earning.</p>
<div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
<button className="btn btn-gradient btn-lg" onClick={() => { track('list_item_click', { position: 'bottom_banner' }); navigate('/list-item') }}>List an item →</button>
<button className="btn btn-white btn-lg" onClick={() => navigate('/how-it-works')}>How it works</button>
</div>
<div style={{ marginTop: 28, display: 'flex', justifyContent: 'center', gap: 16 }}>
<a href="https://www.facebook.com/profile.php?id=61561006256917" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
</svg>
Follow us on Facebook
</a>
</div>
</div>

<footer style={{ background: 'var(--navy)', padding: '2.5rem 1rem' }}>
<div className="container" style={{ maxWidth: 900 }}>
<div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
<a href="/terms" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, textDecoration: 'none' }}>Terms & Conditions</a>
<a href="/privacy" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, textDecoration: 'none' }}>Privacy Policy</a>
<a href="/how-it-works" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, textDecoration: 'none' }}>How it works</a>
<a href="/support" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, textDecoration: 'none' }}>Contact us</a>
<a href="https://www.facebook.com/profile.php?id=61561006256917" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, textDecoration: 'none' }}>Facebook</a>
</div>
<div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
© 2026 HireIt Australia. All rights reserved.
</div>
</div>
</footer>

<BottomNav active="home" />
</div>
)
}

function BottomNav({ active }) {
const navigate = useNavigate()
const items = [
{ Icon: HomeIcon, label: 'Home', path: '/', key: 'home' },
{ Icon: Search, label: 'Browse', path: '/?browse=1', key: 'browse' },
{ Icon: Plus, label: 'List', path: '/list-item', key: 'list' },
{ Icon: Calendar, label: 'Bookings', path: '/my-bookings', key: 'bookings' },
{ Icon: MessageCircle, label: 'Messages', path: '/messages', key: 'messages' },
{ Icon: User, label: 'Profile', path: '/profile', key: 'profile' },
]
return (
<nav className="bottom-nav">
<div className="bottom-nav-inner">
{items.map(({ Icon, label, path, key }) => (
<button key={key} className={`bn-item ${active === key ? 'active' : ''}`} onClick={() => navigate(path)}>
<Icon size={22} strokeWidth={active === key ? 2.5 : 2} className="bn-icon" />
{label}
</button>
))}
</div>
</nav>
)
}